import { useEffect, useState, type FormEvent } from "react";
import { Dialog, FormError, FormField } from "../../components/Dialog";
import { Icon } from "../../components/Icon";
import { useFeedback } from "../../components/Toast";
import { useI18n } from "../../i18n";
import { isTenantAdmin } from "../../lib/permissions";
import { useSession } from "../../state/session";

/**
 * The tenant's controlled-document line for the KYS form PDFs (tenants.form_footer, ADR 0033). null = the
 * neutral default (no company name). `reload` after an admin changed it.
 */
export function useFormFooter(): { footer: string | null; reload: () => void } {
  const { provider, tenant } = useSession();
  const [footer, setFooter] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const tenantId = tenant?.id;
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    provider.getFormFooter(tenantId).then(
      (f) => !cancelled && setFooter(f),
      () => !cancelled && setFooter(null),
    );
    return () => {
      cancelled = true;
    };
  }, [provider, tenantId, tick]);
  return { footer, reload: () => setTick((x) => x + 1) };
}

/** Tenant admins: edit the footer line (empty = the neutral default). */
export function FormFooterButton({ footer, onSaved }: { footer: string | null; onSaved: () => void }) {
  const { t } = useI18n();
  const { provider, tenant, role } = useSession();
  const feedback = useFeedback();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  if (!isTenantAdmin(role) || !tenant) return null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await provider.setFormFooter(tenant.id, text.trim() || null);
      feedback.ok(t("kys.footer.saved"));
      setOpen(false);
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" className="button button-secondary button-sm" onClick={() => (setText(footer ?? ""), setOpen(true))}>
        <Icon name="edit" size={15} /> {t("kys.footer.button")}
      </button>
      {open && (
        <Dialog
          title={t("kys.footer.title")}
          onClose={() => !busy && setOpen(false)}
          onSubmit={submit}
          footer={
            <>
              <button type="button" className="button button-secondary" onClick={() => setOpen(false)} disabled={busy}>
                {t("common.cancel")}
              </button>
              <button type="submit" className="button button-primary" disabled={busy || text.trim().length > 200}>
                {busy ? t("common.working") : t("common.save")}
              </button>
            </>
          }
        >
          <FormField id="kys-footer" label={t("kys.footer.label")} hint={t("kys.footer.hint", { default: t("kys.form.controlledDefault") })} optional>
            <input id="kys-footer" value={text} maxLength={200} onChange={(e) => setText(e.target.value)} data-autofocus />
          </FormField>
          {error !== null && <FormError error={error} />}
        </Dialog>
      )}
    </>
  );
}
